import { Container, Graphics, type Texture } from 'pixi.js';
import { COLORS, DESIGN_W, MOBILE, SPACING, TYPE } from '../app/theme';
import { viewport } from '../app/viewport';
import { Button, makeText, iconSprite } from '../ui/widgets';
import { scaleIn, fadeTo } from '../app/anim';

/** A centered bold gold page title with an engraved emboss and a thin decorative divider beneath. */
export function sceneTitle(title: string, y = 40, icon?: Texture | null): Container {
  const c = new Container();
  const t = makeText(title, { size: TYPE.title, weight: '700', align: 'center', fill: COLORS.gold });
  t.anchor.set(0.5, 0);
  t.position.set(DESIGN_W / 2, y);
  // Engraved-on-wood emboss: a hard dark shadow directly beneath the gold glyphs.
  t.style.dropShadow = { color: 0x2a1c06, alpha: 0.6, blur: 0, distance: 2, angle: Math.PI / 2 };

  // A thin gold divider with a small center gem, a fixed distance below the title baseline.
  const cx = DESIGN_W / 2;
  const divY = y + 58;
  const divider = new Graphics().roundRect(cx - 90, divY, 180, 2, 1).fill({ color: COLORS.accentDark, alpha: 0.9 });
  const gem = new Graphics().rect(-4, -4, 8, 8).fill({ color: COLORS.accent });
  gem.rotation = Math.PI / 4;
  gem.position.set(cx, divY + 1);

  c.addChild(divider, gem, t);

  // Optional icon: laid out together with the title as ONE group centered at cx, so the lockup stays
  // balanced over the (centered) divider instead of the icon hanging off one side. Icon sits on the
  // right — the leading side in RTL — vertically centered on the glyphs. iconSprite() no-ops if missing.
  if (icon) {
    const iconSize = Math.round(TYPE.title * 1.25);
    const gap = 10;
    const textCenterX = cx - (gap + iconSize) / 2;
    t.position.set(textCenterX, y); // shift title left so [title + icon] is centered as a unit
    const node = iconSprite(icon, iconSize);
    node.position.set(textCenterX + t.width / 2 + gap, y + Math.round(TYPE.title * 0.54) - iconSize / 2);
    c.addChild(node);
  }
  return c;
}

/** Visible canvas fallback for Telegram's native header back button. */
export function pageBackButton(onBack: () => void, y = 24): Button {
  const back = new Button('بازگشت', onBack, {
    width: 140,
    height: 72,
    fontSize: 24,
    fill: COLORS.panel,
    textColor: COLORS.text,
    accessibleLabel: 'بازگشت به صفحه قبل',
  });
  back.position.set(24, y);
  return back;
}

/**
 * Shared top navigation for every secondary lobby page. Keeping the title and back action in one
 * component prevents page-specific content (such as the create-table identity card) from shifting
 * the back control to a different vertical position.
 */
export function pageHeader(
  title: string,
  onBack: () => void,
  icon?: Texture | null,
): Container {
  const header = new Container();
  header.addChild(sceneTitle(title, 28, icon), pageBackButton(onBack));
  return header;
}

/** A right-aligned field label (RTL forms). */
export function fieldLabel(text: string, rightX: number, y: number): Container {
  const t = makeText(text, { size: TYPE.label, fill: COLORS.textDim });
  t.anchor.set(1, 0);
  t.position.set(rightX, y);
  return t;
}

/** Standard content geometry for a full-bleed page: 40px gutters, DESIGN_W-80 (640) wide. */
export const PAGE = { left: SPACING.gutter, width: DESIGN_W - SPACING.gutter * 2 } as const;

/** Standard content geometry for a list page: 24px gutters, DESIGN_W-48 (672) wide. */
export const LIST_PAGE = { left: SPACING.gutterSm, width: DESIGN_W - SPACING.gutterSm * 2 } as const;

/**
 * Baseline Y of the bottom «بازگشت» bar, pinned to the **live** viewport bottom (80px up) so it sits
 * at the true screen bottom on tall phones — not the fixed 1280 baseline. The primary action sits a
 * gap above it (see {@link bottomPrimaryY}).
 */
export function bottomBackY(): number {
  return viewport.h - MOBILE.secondaryH - MOBILE.bottomInset;
}

/** Vertical clamp used by the row-spreader. */
function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

/**
 * Evenly distribute `count` form rows down the available height (the "spread + enlarge" look), between
 * `topY` and the bottom button bar, vertically centering the block when the pitch hits its cap. Lets a
 * short form fill a tall screen instead of clustering at the top. Returns the start Y, the chosen
 * pitch, and `rowY(i)` for the i-th row.
 */
export function spreadRows(
  count: number,
  topY: number,
  opts: { reserve?: number; min?: number; max?: number } = {},
): { startY: number; pitch: number; rowY: (i: number) => number } {
  const reserve = opts.reserve ?? 200; // room for the stacked primary + back buttons
  const avail = viewport.h - reserve - topY;
  const pitch = clamp(count > 0 ? avail / count : avail, opts.min ?? 92, opts.max ?? 132);
  const startY = topY + Math.max(0, (avail - pitch * count) / 2);
  return { startY, pitch, rowY: (i: number) => startY + i * pitch };
}

/**
 * Lay out `count` rows at a **fixed** comfortable `pitch` and vertically center the whole block as one
 * unit between `topY` and the bottom reserve — the "container" look (rows stay tightly grouped, only the
 * block's start drifts to center). Unlike {@link spreadRows}, the gaps never stretch on tall screens.
 * Returns the block start Y and `rowY(i)`; `blockH` is the laid-out height for drawing a backing card.
 */
export function centerBlock(
  count: number,
  topY: number,
  pitch: number,
  opts: { reserve?: number } = {},
): { startY: number; blockH: number; rowY: (i: number) => number } {
  const reserve = opts.reserve ?? 200;
  const avail = viewport.h - reserve - topY;
  const blockH = pitch * count;
  const startY = topY + Math.max(0, (avail - blockH) / 2);
  return { startY, blockH, rowY: (i: number) => startY + i * pitch };
}

/** Geometry of the standard bottom button bar — full width, or two equal halves split by SPACING.gap. */
export function bottomBarSlots(opts: { left?: number; width?: number } = {}): {
  left: number;
  width: number;
  half: number;
  rightX: number;
} {
  const left = opts.left ?? PAGE.left;
  const width = opts.width ?? PAGE.width;
  const half = (width - SPACING.gap) / 2;
  return { left, width, half, rightX: left + half + SPACING.gap };
}

/**
 * Pin 1–2 already-built buttons to the bottom bar at the shared baseline. One button → placed at the
 * left gutter (build it full-width). Two buttons → array order [right, left]; build each at the
 * `half` width from {@link bottomBarSlots}. Positions the buttons in place and returns them; the
 * caller still `addChild`s them (so onTap/labels/fills stay scene-owned).
 */
export function bottomBar(buttons: Button[], opts: { left?: number; width?: number; y?: number } = {}): Button[] {
  const { left, rightX } = bottomBarSlots(opts);
  const y = opts.y ?? bottomBackY();
  if (buttons.length === 1) {
    buttons[0].position.set(left, y);
  } else if (buttons.length >= 2) {
    buttons[0].position.set(rightX, y); // first = right (RTL-friendly)
    buttons[1].position.set(left, y);
  }
  return buttons;
}

/** ScrollList height that fills from `topY` down to the reserved bottom strip (floored at 120). */
export function listHeight(topY: number, reserve = 88): number {
  return Math.max(120, viewport.h - topY - reserve);
}

/**
 * Y for a primary action button of height `h`, pinned a consistent gap above the bottom «بازگشت» bar.
 * Height-aware so a tall (78px) submit and a short (64px) save both clear the back button by the same
 * margin — unlike a flat constant, which made a tall submit collide with the back button.
 */
export function bottomPrimaryY(h: number, gap = 16): number {
  return bottomBackY() - gap - h;
}

/**
 * Subtle staggered fade-in for a menu page's primary elements — safe for any layout (no position
 * shift). Presets each node to alpha 0, then fades it to 1 on a small per-index delay.
 */
export function enterFade(nodes: Container[], step = 50, duration = 260): void {
  nodes.forEach((n, i) => {
    n.alpha = 0;
    fadeTo(n, 1, duration, step * i);
  });
}

/** Pop a set of hero nodes (mode tiles / avatars) in from a slight scale, with a subtle stagger. */
export function enterPop(nodes: Container[], step = 60, from = 0.9): void {
  nodes.forEach((n, i) => scaleIn(n, 260, from, step * i));
}
